import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { HubConnection } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Constants } from 'src/@smartmanufacturing/common/constants';
import { JWTTokenValidation } from 'src/@smartmanufacturing/common/enum';
import { SharedConstants } from 'src/app/user-management/shared/models/shared-constants';
import { UserDeviceService } from './user-device.service';
import { JwtDecoderService } from '../helpers/jwt-decoder.service';
import { NotificationItem } from '../models/notifications/notification-item';
import { NotificationService } from './notification.service';
import { NextBestActionDto } from '../models/batch-qc-preparation-model';


@Injectable()
export class MessagingService implements OnDestroy {
  public currentMessage$ = new BehaviorSubject(null);
  public currentMessageFromFirebase$ = new BehaviorSubject(null);
  public notificationToken$ = new BehaviorSubject(SharedConstants.INIT_VALUE);
  public nextBestAction$ = new BehaviorSubject<NextBestActionDto>(null);
  public nextBestReminderAction$ = new BehaviorSubject<NextBestActionDto>(null);
  private destroy$ = new Subject();
  private hubConnectionBuilder!: HubConnection;
  private dataTransferHubConnection!: HubConnection;
  public signalRKey: string = '';
  signalrKeys: string[] = [];
  private nextBestHubConnection!: HubConnection;
  private nextBestReminderHubConnection!: HubConnection;
  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private notificationService: NotificationService,
    private userDeviceService: UserDeviceService,
    private jwtDecoderService: JwtDecoderService
  ) { }

  ngOnDestroy() {
    this.destroy$.complete();
  }

  requestPermission(signalRKey:string) {
    this.angularFireMessaging.requestToken.pipe(takeUntil(this.destroy$)).subscribe({
      next: (token) => {
        this.notificationToken$.next(token);
        console.log('Firebase Connection started.......!')
        const timezoneOffset = (-1 * new Date().getTimezoneOffset());
        let userDevice = this.userDeviceService.getCurrentUserDevice();
        if (!userDevice) {
          userDevice = this.userDeviceService.generateNewDevice();
        }
        const data = {
          NotificationToken: token ? token : "init",
          OsVersion: userDevice.osVersion,
          DeviceModel: userDevice.deviceModel,
          AppVersion: userDevice.appVersion,
          OsType: userDevice.osType,
          DeviceId: userDevice.deviceId,
          TimeZone: timezoneOffset,
          signalRKey: signalRKey
        }
        this.userDeviceService.registerUserDevice(data).subscribe({
          next: (response) => {
            this.userDeviceService.saveUserDevice(response);
            if (this.checkUserLoggin()) {
              this.editUserDevice(signalRKey);
            }
          },
          error: ({ error }) => {
            console.log(error?.message);
          }
        })
      },
      error: (err) => {
        console.error('Unable to get permission to notify.', err);
        this.notificationToken$.next(SharedConstants.NOT_SUPPORTED);
      }
    });
  }

  receiveMessage() {
    this.angularFireMessaging.messages.pipe(takeUntil(this.destroy$)).subscribe({
      next: (payload: any) => {
        const firebaseMessage = {
          ...payload?.data,
          id: parseInt(payload?.data?.id, 10),
          isRead: payload?.data?.isRead === 'TRUE' ? true : false
        };
        this.currentMessageFromFirebase$.next(firebaseMessage);
      }
    });
  }

  InitializeSignalR(): void {
    this.hubConnectionBuilder = new signalR.HubConnectionBuilder().withUrl(Constants.API_SIGNALR_BASE_URL + 'notifications').configureLogging(signalR.LogLevel.Information).build();
    console.log("SignalR URL For Notification", Constants.API_SIGNALR_BASE_URL + 'notifications');
    this.dataTransferHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(Constants.API_SIGNALR_BASE_URL + 'datatransfer')
      .configureLogging(signalR.LogLevel.Information)
      .build();
    this.hubConnectionBuilder.start().then(() => {
      console.log('SignalR Connection started for Push Notification .......!')
      this.signalRKey = this.hubConnectionBuilder.connectionId;
      this.signalrKeys.push(this.signalRKey);
      console.log("key", this.signalRKey);
      this.requestPermission(this.signalRKey);
    }).catch(err => console.log('Error while connect with signalR  Auth server'));
    this.dataTransferHubConnection.start().then(() => {
      console.log('SignalR Connection started with data transfer .......!')
      this.signalRKey = this.dataTransferHubConnection.connectionId;
      this.signalrKeys.push(this.signalRKey);
      console.log("key", this.signalRKey);
      this.requestPermission(this.signalRKey);
    }).catch(err => console.log('Error while connect with signalR  Auth server'));
    this.nextBestHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(Constants.API_SIGNALR_BASE_URL + 'nextbestaction')
      .configureLogging(signalR.LogLevel.Information)
      .build();
    this.nextBestReminderHubConnection = new signalR.HubConnectionBuilder()
      .withUrl(Constants.API_SIGNALR_BASE_URL + 'nextbestaction')
      .configureLogging(signalR.LogLevel.Information)
      .build();
    this.nextBestHubConnection.start().then(() => {

      console.log('SignalR Connection started with Next Best Action .......!')
      this.signalRKey = this.nextBestHubConnection.connectionId;
      this.signalrKeys.push(this.signalRKey);
      console.log("key", this.signalRKey);
      this.requestPermission(this.signalRKey);
    }).catch(err => console.log('Error while connect with signalR  Auth server'));
    this.nextBestReminderHubConnection.start().then(() => {

      console.log('SignalR Connection started with Next Best Reminder .......!')
      this.signalRKey = this.nextBestReminderHubConnection.connectionId;
      this.signalrKeys.push(this.signalRKey);
      console.log("key", this.signalRKey);
      this.requestPermission(this.signalRKey);
    }).catch(err => console.log('Error while connect with signalR  Auth server'));
  }


  recieveNotificationMessageFromSignalR(): void {
    this.hubConnectionBuilder.on('SendNotificationToUsers', (result: any) => {
      console.log('Received SignalR notification For Push Notification:', result);
      this.signalRImplementation(result);
    });
    this.nextBestHubConnection.on('SendNextBestActionDataToUsers', (result: any) => {
      console.log('Received SignalR notification For Next Best Action:', result);
      this.nextBestAction$.next(result);
    });
    this.nextBestReminderHubConnection.on('SendNextBestActionReminderDataToUsers', (result: any) => {
      console.log('Received SignalR notification For Next Best Reminder Action:', result);
      this.nextBestReminderAction$.next(result);
    });
  }

  resetNextBestAction(): void {
    const emptyNextBestAction: NextBestActionDto = {
      UserCode: '',       
      NotificationTitle: '',  
      content: '',        
      link: '',           
      isBot: false,       
      time: '',           
      isRead: false       
    };
    this.nextBestAction$.next(emptyNextBestAction);
  }

  signalRImplementation(result: any): void {
    const notification = new NotificationItem();
    notification.id = result['id'];
    notification.title = result['title'];
    //Add By Umair Ali For CilConfiguration Bulk Upload Error File Download  --Start
    let desc = result['description'];
    if (desc && desc.startsWith('Download error file:')) {
      desc = 'Click to download the error file.'; 
    }
    if(desc && desc.startsWith("Bulk upload completed with errors") &&
        desc?.includes("https://"))
    {
      desc = desc.replace(/Download error file:.*$/i, '').trim();
    }
    let downloadUrl: string | null = null;
    //Add By Umair Ali For CilConfiguration Bulk Upload Error File Download  --Start
    if (
      result.description?.includes("Bulk upload completed with errors") &&
      result.description?.includes("https://")
    ) {
      const urlMatch = result.description.match(/https?:\/\/\S+/);
      if (urlMatch) {
        downloadUrl = urlMatch[0];
      }
    }
    //Add By Umair Ali For CilConfiguration Bulk Upload Error File Download  --End
    result['description'] = desc;
    notification.body = result['description'];
    //Add By Umair Ali For CilConfiguration Bulk Upload Error File Download  --End
    notification.type = result['notificationType'];
    notification.elementId = result['elementId'];

    if (document.getElementsByClassName('noticePopupContainer').length == 0) {

      let mainDiv = document.createElement('div');
      mainDiv.style.display = 'flex';
      mainDiv.style.flexDirection = 'column';
      mainDiv.style.position = 'absolute';
      mainDiv.style.top = '22px';
      mainDiv.style.right = '12px';
      mainDiv.style.zIndex = '10000';
      mainDiv.setAttribute('class', 'noticePopupContainer');
      document.body.prepend(mainDiv);
    }
    

    let el = document.createElement('div');
    el.setAttribute('class', 'noticePopup');

      const downloadLinkHtml = downloadUrl     
    ? `<a href="${downloadUrl}" target="_blank" 
          style="color: rgb(11, 11, 242); text-decoration: underline;" 
          download>
         Download Error File
       </a>`
    : '';
    el.innerHTML = `<div class="notification_card" *ngFor="let message of messages;">
    <h3 [matTooltip]="message?.title">${result.title}</h3>
    ${downloadLinkHtml}
    <p [matTooltip]="message?.description">${result.description}</p>
    <p class="text-xs text-green-600 leading-none mt-1">${result.createdAt}
    </p> 
  </div>`
    el.style.position = 'relative';
    el.style.display = 'flex';
    el.style.marginLeft = 'auto';
    el.style.opacity = '0%';
    el.style.marginTop = '12px';

    let duplicateFound = false;

    if (duplicateFound == false) {
      document.getElementsByClassName('noticePopupContainer')[0].appendChild(el);

      let opacCount = 0;
      let interval = setInterval(() => {
        if (opacCount == 100) {
          clearInterval(interval);
        }
        else {
          opacCount += 10;
          el.style.opacity = opacCount + '%';
        }
      }, 60);


      setTimeout(() => {
        el.remove();

      }, 5000);


      this.notificationService.showNotification(notification);
      this.currentMessage$.next(result);
    }
  }


  editUserDevice(signalRKey:string = ''): void {
    const timezoneOffset = (-1 * new Date().getTimezoneOffset());
    let userDevice = this.userDeviceService.getCurrentUserDevice();
    if (!userDevice) {
      userDevice = this.userDeviceService.generateNewDevice();
    }
    const data = {
      NotificationToken: userDevice.notificationToken,
      OsVersion: userDevice.osVersion,
      DeviceModel: userDevice.deviceModel,
      AppVersion: userDevice.appVersion,
      OsType: userDevice.osType,
      DeviceId: userDevice.deviceId,
      TimeZone: timezoneOffset,
      signalRKeys:signalRKey === "SignIn" ? this.signalrKeys : [signalRKey] 
    };
    this.notificationToken$.subscribe({
      next: (token) => {
        if (!data.NotificationToken) {
          data.NotificationToken = token;
        }
        this.userDeviceService.editUserDevice(data).subscribe({
          next: (response) => {
            this.userDeviceService.saveUserDevice(response);
          },
          error: ({ error }) => {
            console.log(error?.message);
          }
        })
      }
    });
  }

  checkUserLoggin() {
    const isValidToken: JWTTokenValidation = this.jwtDecoderService.isThereValidToken();
    if (isValidToken === JWTTokenValidation.Expired || isValidToken === JWTTokenValidation.NotFound) {
      return false;
    }
    return true;
  }

}
